import React from 'react';
import type { TFunction } from 'i18next';

import { fromDateTimeType } from '@/js/libs/api-helpers';
import { Link } from '@/js/libs/ui/src/V2';

import { ROUTER } from 'src/AppRouter';
import { DATE_TIME_FORMAT } from 'src/constants';
import type { Column } from '../../../types';
import { ActivityCell, PriorityCell, StatusCell, LabelsCell } from '../../cells';

export const useColumns = (t: TFunction) =>
  React.useMemo<Column[]>(
    () => [
      {
        name: 'id',
        title: t('common:ID'),
        component: ({ item }) =>
          item.id && <Link to={ROUTER['/sales/tasks/details/:taskId'].path({ taskId: item.id.toString() })}>{item.id.toString()}</Link>,
        sortable: true,
      },
      {
        name: 'priority',
        title: t('serviceDashboard:PRIORITY'),
        component: PriorityCell,
        sortable: true,
        dataTestId: 'priority-header',
      },
      {
        name: 'activity',
        title: t('serviceDashboard:ACTIVITY'),
        component: ActivityCell,
      },
      {
        name: 'account',
        title: t('common:ACCOUNT'),
        component: ({ item }) =>
          item.account?.id && (
            <Link to={ROUTER['/crm/accounts/:accountId'].path({ accountId: item.account.id.toString() })}>{item.account?.displayName}</Link>
          ),
        breakWord: true,
      },
      {
        name: 'createdDate',
        title: t('common:CREATED'),
        component: ({ item }) => fromDateTimeType(item.createdDate)?.local().format(DATE_TIME_FORMAT),
        sortable: true,
        dataTestId: 'created-header',
      },
      {
        name: 'leadSource.name',
        title: t('serviceDashboard:TASK_SOURCE'),
        component: ({ item }) => item.leadSource.name,
        sortable: true,
        breakWord: true,
        dataTestId: 'lead-source-header',
      },
      {
        name: 'taskTopic',
        title: t('serviceDashboard:TASK_TOPIC'),
        component: ({ item }) => item.taskTopic?.name,
        sortable: true,
        breakWord: true,
        dataTestId: 'task-topic-header',
      },
      {
        name: 'campaign',
        title: t('common:CAMPAIGN'),
        component: ({ item }) =>
          item.campaign?.name &&
          item.campaign.campaignId && (
            <Link
              to={ROUTER['/orders/campaigns/:type/:campaignId'].path({
                type: 'ALL',
                campaignId: item.campaign.campaignId.toString(),
              })}>
              {item.campaign.name}
            </Link>
          ),
        breakWord: true,
      },
      {
        name: 'assigneeContact',
        title: t('serviceDashboard:ASSIGNEE'),
        component: ({ item }) => item.assigneeContact?.name,
        breakWord: true,
      },
      {
        name: 'labels',
        title: t('common:LABELS'),
        component: LabelsCell,
        breakWord: true,
      },
      {
        name: 'status',
        title: t('common:STATUS'),
        component: StatusCell,
        sortable: true,
        dataTestId: 'status-header',
      },
    ],
    [t],
  );
